<template>
  <div ref="rootRef" class="flex w-full flex-col justify-center py-2">
    <SubQuestionHeader v-if="!isFocused" :content="question.content" :skills="question.skills" />
    <div class="mb-2 flex w-full flex-row items-center justify-center">
      <span class="text-body-small md:text-body-medium text-on-surface-variant text-center">
        Für eine genaue Einschätzung wähle pro Schlagwort die Stufe, die am besten passt. <br />
        Passt ein Beispiel nicht zur Unterrichtsstunde? Dann kannst du es weglassen.
      </span>
    </div>
    <div class="flex flex-col gap-1">
      <div
        class="ease-fast-effect flex flex-col gap-2 transition-all duration-150"
        :class="[
          isFocused ? 'pointer-events-none -translate-y-3 opacity-0' : 'translate-y-0 opacity-100',
        ]"
      >
        <SubQuestion
          v-for="subQuestion in question.subQuestions"
          :key="subQuestion.id"
          :model-value="getSubQuestionValue(subQuestion.id)"
          @update:model-value="(value) => setSubQuestionValue(subQuestion.id, value)"
          :question="subQuestion"
          :scale="scale"
        />
        <div class="text-outline/80 mt-10 mb-14 flex w-full flex-col items-center justify-center">
          <span class="text-title-small">Gesamteinschätzung</span>
          <BaseIcon>keyboard_double_arrow_down</BaseIcon>
        </div>
      </div>
      <div
        ref="overallRef"
        class="rounded-large ease-spatial p-4 transition-transform duration-500 will-change-transform"
        :class="[!isFocused && 'bg-surface-container']"
        :style="{ transform: `translateY(${isFocused ? focusOffset : 0}px)` }"
      >
        <div
          v-if="isFocused"
          class="text-outline/80 absolute -top-10 left-1/2 flex w-full -translate-x-1/2 flex-col items-center justify-center"
        >
          <BaseIcon>keyboard_double_arrow_up</BaseIcon>
          <span class="text-title-small">Schlagworte</span>
        </div>
        <div class="mb-4 flex flex-row items-center gap-1 pt-10">
          <div
            v-for="skill in question.skills"
            :key="skill.id"
            class="rounded-medium flex flex-row items-center gap-2 px-3 py-1"
            :class="[`bg-${skill.color}-90`, `text-${skill.color}-40`]"
          >
            <span class="h-3 w-3 rounded-full" :class="[`bg-${skill.color}-60`]"></span>
            <span class="text-body-large">{{ skill.title }}</span>
          </div>
        </div>
        <h3 class="text-headline-small md:text-headline-large mb-10">
          {{ question.content }}
        </h3>

        <ImageButtonScale
          v-if="scale.type == ScaleType.ImageButtons"
          v-model="feedback"
          :scale="scale"
          :hide-numbers="true"
          :identifier="question.id"
          :suggested-value="roundedSubQuestionAverage?.value"
        />
        <div
          v-if="roundedSubQuestionAverage"
          class="mt-6 flex flex-col items-center px-14 text-center"
        >
          <span class="text-body-small md:text-body-medium text-on-surface-variant">
            Der Durchschnitt deiner Einschätzungen ist
            <span class="text-primary font-medium">«{{ roundedSubQuestionAverage?.label }}»</span>.
            Wenn ein Punkt für dich besonders wichtig war, kannst du das Rating ensprechend
            anpassen.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { SubFeedback } from '../../models/creatable-feedback'
  import { ScaleType, type ImageButtonScale as ImageButtonScaleType } from '@/core/models/scale'
  import SubQuestion from './SubQuestion.vue'
  import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
  import SubQuestionHeader from './SubQuestionHeader.vue'
  import ImageButtonScale from '../scale-based-questions/ImageButtonScale.vue'
  import { useScrollFocusAtBottom } from '@/core/composables/use-scroll-focus-at-bottom'
  import type { Skill } from '@/core/models/skill.ts'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'

  const props = defineProps<{
    question: {
      readonly id: string
      readonly content: string
      readonly scaleItemDescriptions?: ReadonlyArray<string>
      readonly skills: ReadonlyArray<Skill>
      readonly subQuestions?: ReadonlyArray<{
        readonly id: string
        readonly content: string
        readonly scaleItemDescriptions?: ReadonlyArray<string>
      }>
    }
    scale: ImageButtonScaleType
    subFeedbacks?: SubFeedback[]
  }>()

  const emit = defineEmits<{
    'update:subFeedbacks': [subFeedbacks: SubFeedback[]]
  }>()

  const feedback = defineModel<number>()

  const rootRef = useTemplateRef<HTMLElement>('rootRef')
  const overallRef = useTemplateRef<HTMLElement>('overallRef')

  const { isAtBottom, scrollParent } = useScrollFocusAtBottom(rootRef, 120)
  const isFocused = computed(() => isAtBottom.value)

  const focusOffset = ref(0)

  const centerOverallBox = (currentlyApplied: number) => {
    const box = overallRef.value
    if (!box) return

    const rect = box.getBoundingClientRect()
    const parent = scrollParent.value

    const remainingScroll = parent
      ? Math.max(0, parent.scrollHeight - parent.clientHeight - parent.scrollTop)
      : 0

    const naturalCenter = rect.top + rect.height / 2 - currentlyApplied - remainingScroll

    const viewportCenter = parent
      ? parent.getBoundingClientRect().top + parent.clientHeight / 2
      : window.innerHeight / 2

    focusOffset.value = viewportCenter - naturalCenter
  }

  watch(isFocused, (focused) => {
    if (focused) {
      nextTick(() => centerOverallBox(0))
    } else {
      focusOffset.value = 0
    }
  })

  const handleResize = () => {
    if (isFocused.value) centerOverallBox(focusOffset.value)
  }

  onMounted(() => window.addEventListener('resize', handleResize))
  onUnmounted(() => window.removeEventListener('resize', handleResize))

  const subQuestionFeedbackValues = ref<{ value: number | null; subQuestionId: string }[]>([])
  const skipInitialAutoSuggest = ref(feedback.value !== undefined)

  const initSubQuestionValues = () => {
    for (const subQuestion of props.question.subQuestions ?? []) {
      const existing = subQuestionFeedbackValues.value.find(
        (entry) => entry.subQuestionId === subQuestion.id,
      )
      if (existing?.value != null) continue

      const storedValue =
        props.subFeedbacks?.find((entry) => entry.questionId === subQuestion.id)?.value ?? null

      if (existing) {
        existing.value = storedValue
      } else {
        subQuestionFeedbackValues.value.push({
          subQuestionId: subQuestion.id,
          value: storedValue,
        })
      }
    }
  }

  onMounted(initSubQuestionValues)

  const emitSubFeedbacks = () => {
    emit(
      'update:subFeedbacks',
      subQuestionFeedbackValues.value
        .filter((entry): entry is { subQuestionId: string; value: number } => entry.value != null)
        .map((entry) => ({ questionId: entry.subQuestionId, value: entry.value })),
    )
  }

  const getSubQuestionValue = (subQuestionId: string) => {
    return (
      subQuestionFeedbackValues.value.find((entry) => entry.subQuestionId === subQuestionId)
        ?.value ?? null
    )
  }

  const setSubQuestionValue = (subQuestionId: string, value: number | null) => {
    const entry = subQuestionFeedbackValues.value.find(
      (item) => item.subQuestionId === subQuestionId,
    )
    if (entry) {
      entry.value = value
    } else {
      subQuestionFeedbackValues.value.push({ subQuestionId, value })
    }
    emitSubFeedbacks()
  }

  const subQuestionAverage = computed(() => {
    const answeredValues = subQuestionFeedbackValues.value
      .map((entry) => entry.value)
      .filter((value): value is number => value != null)

    if (answeredValues.length === 0) return null

    return answeredValues.reduce((acc, value) => acc + value, 0) / answeredValues.length
  })

  watch(subQuestionAverage, () => {
    if (skipInitialAutoSuggest.value) {
      skipInitialAutoSuggest.value = false
      return
    }
    feedback.value = roundedSubQuestionAverage.value?.value
  })

  const roundedSubQuestionAverage = computed(() => {
    const avg = subQuestionAverage.value
    if (avg === null) return null
    // Find the scale item with value nearest to the average
    let closestItem = props.scale.items[0]
    let minDiff = Math.abs(avg - closestItem.value)
    for (const item of props.scale.items) {
      const diff = Math.abs(avg - item.value)
      if (diff < minDiff) {
        closestItem = item
        minDiff = diff
      }
    }
    return closestItem
  })
</script>
